import { Injectable } from '@angular/core';
import { ToastrService, IndividualConfig, ToastRef, ActiveToast } from 'ngx-toastr';
import { Subject ,  Observable } from 'rxjs';
import { AppMessage } from './app-message';
import { environment } from '../../../environments/environment';
import { StateService } from '../state/state.service';
import { QmReplacePipe } from '../../components/qm-pipes/qm-replace.pipe';
import { TranslateService } from '@ngx-translate/core';
import { Reminder } from '../../reminder/reminder.model';
import { _ } from '../../shared/utils';
import { format } from 'date-fns';
import fi from 'date-fns/locale/fi';
import { PushNotificationsService } from './push-notifications.service';
import { SidebarService } from '../sidebar/sidebar.service';
import { NotificationEntry } from '~app/components/notification/notification.model';
import { VivoNotification } from '~app/components/notification/new-notification/vivo-notification/vivo-notification.model';
import { Comment } from '~app/components/notification/comment/comment.model';

@Injectable()
export class AppMessageService {

  /**
   * alarm listener Subject
   * @type {Subject<any>}
   */
  private alarmData = new Subject<AppMessage>();
  /**
   * Observable for alarm listener Subject
   * @type {Observable<any>}
   */
  public alarmChanged$: Observable<AppMessage> = this.alarmData.asObservable();

   /**
   * References to currently displayed messages by reminder id.
   */
  public reminders = new Map<number, {toastRef: ToastRef<any>, notificationRef: Notification}>();

  constructor(
    private toastr: ToastrService,
    private stateService: StateService,
    private push: PushNotificationsService,
    private translate: TranslateService,
    private sidebarService: SidebarService) {
  }

  /**
   * Shows a push notification on the desktop if the browser isn't visible.
   * Always shows a toast notification inside the app.
   * Syncs dismissing both kinds of notifications.
   */
  private pushAndToast({title, body, variant, skipPush, toastConfig, onClick, onShow, onHide}: {
    title: string,
    body: string,
    variant: string,
    skipPush?: boolean,
    toastConfig?: Partial<IndividualConfig>,
    onClick?: () => void,
    onShow?: (notification: Notification | null, toast: ActiveToast<any>) => void,
    onHide?: () => void,
  }) {
    const showToast = (notification: Notification | null): ActiveToast<any> => {
      const toast = this.toastr.show(body, title, {
        closeButton: true,
        tapToDismiss: false,
        ...toastConfig
      }, `toast-${variant}`);

      onShow?.(notification, toast);

      toast.onTap.subscribe(() => {
        onClick?.();
      });
      toast.onHidden.subscribe(() => {
        onHide?.();
        notification?.close();
      });

      return toast;
    }

    if (skipPush || document.visibilityState === 'visible' || !this.push.hasPermission()) {
      showToast(null);
      return;
    }

    let toast: ActiveToast<any>;
    this.push.create(`REGO ${title}`, {
      body,
      icon: environment.assetRoot + '/favicon/192.png',
      requireInteraction: toastConfig?.disableTimeOut === true,
    }).subscribe({
      next: ({ notification, event }) => {
        switch (event.type) {
          case 'show':
            toast = showToast(notification);
            break;
          case 'click':
            onHide = null; // prevent toast from calling `onHide` when it's closed
            toast?.toastRef.close();
            onClick?.();
            break;
        }
      },
      // If notification was rejected (no permissions for instance)
      error: (err) => {
        console.log(err);
        showToast(null);
      },
      complete: () => {
        toast?.toastRef.close();
      }
    });
  }

  /**
   * Add new status listener
   * @param alarmMessage
   */
  public broadcastAlarm(alarmMessage: AppMessage) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      alarmMessage.message = this.getTranslation(alarmMessage.message);
      /// NOTE: info level messages are not translated ?
      const toastConfigurationForAlarm: Partial<IndividualConfig> = {
        timeOut: 10 * 1000,
        closeButton: true,
        tapToDismiss: false
      };

      if (alarmMessage.toast) {
        switch (alarmMessage.priority) {
          case 'success':
            if (alarmMessage.translations) {
              alarmMessage.message = new QmReplacePipe().transform(alarmMessage.message, alarmMessage.translations);
            }
            this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-success').onTap.subscribe(() => this.handleToastTapped(alarmMessage));
            break;
          case 'warning':
            if (alarmMessage.translations) {
              alarmMessage.message = new QmReplacePipe().transform(alarmMessage.message, alarmMessage.translations);
            }
            this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-warning').onTap.subscribe(() => this.handleToastTapped(alarmMessage));
            break;
          case 'error':
            if (alarmMessage.translations) {
              alarmMessage.message = new QmReplacePipe().transform(alarmMessage.message, alarmMessage.translations);
            }
            this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-custom-error').onTap.subscribe(() => this.handleToastTapped(alarmMessage));
            break;
          default:
            this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-info').onTap.subscribe(() => this.handleToastTapped(alarmMessage));
            break;
        }
      }
    }
    this.alarmData.next(alarmMessage);
  }

  public broadcastNotification(notification: NotificationEntry) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      this.pushAndToast({
        title: this.getTranslation(_('Message')),
        body: notification.description,
        variant: 'info',
        toastConfig: { disableTimeOut: true },
        skipPush: !this.stateService.getPartOfState('settingsState').pushNotifications,
        onClick: () => {
          this.sidebarService.requestSidebarPanel('Notification');
        }
      });
    }
  }

  public broadcastNotificationComment(comment: Comment) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      this.pushAndToast({
        title: this.getTranslation(_('Response')),
        body: comment.comment,
        variant: 'info',
        toastConfig: { disableTimeOut: true },
        skipPush: !this.stateService.getPartOfState('settingsState').pushNotifications,
        onClick: () => {
          this.sidebarService.requestSidebarPanel('Notification');
        }
      });
    }
  }

  public broadcastDiary(notification: VivoNotification) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      this.pushAndToast({
        title: this.getTranslation(_('Diary')),
        body: notification.message,
        variant: 'info',
        toastConfig: { disableTimeOut: true },
        skipPush: !this.stateService.getPartOfState('settingsState').pushDiary,
        onClick: () => {
          this.sidebarService.requestSidebarPanel('Diary');
        }
      });
    }
  }

  public broadcastReminder(reminder: Reminder) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      let title = this.getTranslation(_('Reminder'));
      if (reminder.backendReminder.alarmingTime) {
        title += ' ';
        title += this.formatDate(reminder.backendReminder.alarmingTime);
      }

      if (this.reminders.has(reminder.backendReminder.id)) {
        return;
      } else {
        this.reminders.set(reminder.backendReminder.id, {notificationRef: null, toastRef: null});
      }

      this.pushAndToast({
        title,
        body: reminder.backendReminder.description,
        variant: 'reminder',
        toastConfig: { disableTimeOut: true },
        onShow: (notification, toast) => {
          this.reminders.set(reminder.backendReminder.id, {notificationRef: notification, toastRef: toast.toastRef});
        },
        onHide: () => {
          reminder.cancelAlarm();
        }
      });
    }
  }

  public hideReminder(reminder: Reminder) {
    if (this.reminders.has(reminder.backendReminder.id)) {
      const {notificationRef, toastRef} = this.reminders.get(reminder.backendReminder.id);
      this.reminders.delete(reminder.backendReminder.id);
      if (toastRef) {
        toastRef.close();
      }
      if (notificationRef) {
        notificationRef.close();
      }
    }
  }

  private getTranslation(msg: string): string {
    try {
      return this.translate.instant(msg);
    } catch (e) {
      return msg;
    }
  }

  private formatDate(value: Date): string {
    const form = 'HH:mm';
    if (this.stateService.getPartOfState('settingsState').language === 'fi') {
      return format(value, form, {locale: fi});
    } else {
      return format(value, form);
    }
  }

  private handleToastTapped(appMessage: AppMessage) {
    this.sidebarService.requestSidebarPanel(appMessage.component);
  }
}
